// src/ui/screens/post/StatusScreen.jsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../../firebase';
import UserAvatar from '../../../components/UserAvatar';
import {
  StatusWantService,
  StatusWantType,
} from '../../../services/statusWantService';

const kOrange = '#FF8C08';
const kCard = '#23262C';

const PER_STATUS_MS = 6000;

// Desktop layout: matches the `lg` (1024px) Tailwind breakpoint used elsewhere.
const DESKTOP_QUERY = '(min-width: 1024px)';
const DESKTOP_STAGE_HEIGHT = 'min(92vh, 860px)';
const DESKTOP_CARD_WIDTH = `calc(${DESKTOP_STAGE_HEIGHT} * 9 / 16)`;

/**
 * TikTok/WhatsApp-style full-screen story viewer for one user's active
 * status posts, rendered as an overlay by PostScreen.
 *
 * Props:
 *  - uid            owner of the statuses to play
 *  - onClose(ids)   called with the doc ids viewed this session
 *  - onOpenProfile  called with the owner's uid
 *
 * Auto-advances every few seconds; tapping the left/right edge steps
 * back/forward; swipe down or the close button dismisses.
 *
 * Desktop (>=1024px): the story plays in a centered 9:16 card, with
 * prev/next arrows on the sides, the like/share/delete actions in a
 * column beside the card, and the close button pinned to the top-right.
 */
export default function StatusViewerScreen({ uid, onClose, onOpenProfile }) {
  const [docs, setDocs] = useState([]);
  const [index, setIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // On web, auth.currentUser can be null until Firebase restores the
  // session, so track it instead of reading it once at render time.
  const [authReady, setAuthReady] = useState(!!auth.currentUser);
  const [myUid, setMyUid] = useState(auth.currentUser?.uid ?? null);

  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia(DESKTOP_QUERY).matches
  );

  const viewedThisSession = useRef(new Set());
  const elapsedRef = useRef(0);
  const touchStartY = useRef(0);
  const goNextRef = useRef(() => {});

  const isOwner = !!uid && uid === myUid;

  // ── Viewport ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setMyUid(user?.uid ?? null);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────

  const ownerName =
    ownerProfile?.displayName ?? ownerProfile?.name ?? 'User';

  const ownerUsername = (() => {
    const u = ownerProfile?.username?.toString() ?? '';
    if (!u) return '';
    return u.startsWith('@') ? u : `@${u}`;
  })();

  const ownerAvatarUrl =
    ownerProfile?.profileImage && ownerProfile.profileImage.toString().length > 0
      ? ownerProfile.profileImage.toString()
      : ownerProfile?.photoURL ?? null;

  const currentTimeLabel = (() => {
    if (docs.length === 0 || index >= docs.length) return '';
    const ts = docs[index].data()?.createdAt;
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return StatusWantService.relativeTime(date);
  })();

  // Same as Flutter's Navigator.pop(viewedThisSession): hand the viewed
  // ids back to PostScreen so the rings grey out immediately.
  const handleClose = useCallback(() => {
    if (onClose) onClose(Array.from(viewedThisSession.current));
  }, [onClose]);

  // ── Load ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!authReady) return;

    if (!uid) {
      console.warn('[StatusViewer] called with no uid');
      setLoadError('No user specified.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [activeDocs, profileSnap] = await Promise.all([
          StatusWantService.activeDocsForUser(StatusWantType.status, uid),
          getDoc(doc(db, 'users', uid)).catch((e) => {
            console.warn('[StatusViewer] profile load failed', e);
            return null;
          }),
        ]);

        if (cancelled) return;

        // Open at the first status this user hasn't seen yet.
        let startIndex = 0;
        if (!isOwner) {
          for (let i = 0; i < activeDocs.length; i++) {
            const seen = await StatusWantService.hasViewed(
              StatusWantType.status,
              activeDocs[i].id
            );
            if (!seen) {
              startIndex = i;
              break;
            }
          }
        }

        if (cancelled) return;

        setDocs(activeDocs);
        setIndex(startIndex);
        setOwnerProfile(
          profileSnap && profileSnap.exists() ? profileSnap.data() : null
        );
      } catch (e) {
        console.error('[StatusViewer] load failed', e);
        if (!cancelled) {
          setLoadError(e?.message ?? 'Could not load status.');
        }
      } finally {
        // Always clear the spinner.
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, authReady, myUid]);

  // ── Play current ─────────────────────────────────────────────────────

  const playCurrent = useCallback(() => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;

    elapsedRef.current = 0;
    setProgress(0);
    setIsPaused(false);

    if (!isOwner) {
      const docId = current.id;
      viewedThisSession.current.add(docId);
      StatusWantService.markViewed(StatusWantType.status, docId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, index, isOwner]);

  useEffect(() => {
    if (docs.length === 0) return;
    if (!docs[index]) return;
    playCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, docs.length]);

  // Load like state for the current status
  useEffect(() => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    let cancelled = false;
    const docId = current.id;
    (async () => {
      const liked = await StatusWantService.hasLiked(
        StatusWantType.status,
        docId
      );
      if (!cancelled) setIsLiked(liked);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, docs.length]);

  // Progress timer — accumulates elapsed time so pausing (press-and-hold,
  // sheets, dialogs) resumes where it left off instead of restarting.
  useEffect(() => {
    if (isLoading || docs.length === 0 || isPaused) return;
    let last = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      elapsedRef.current += now - last;
      last = now;
      const p = Math.min(elapsedRef.current / PER_STATUS_MS, 1);
      setProgress(p);
      if (p >= 1) {
        clearInterval(timer);
        goNextRef.current();
      }
    }, 50);
    return () => clearInterval(timer);
  }, [index, isLoading, isPaused, docs.length]);

  // ── Navigation ───────────────────────────────────────────────────────

  const goNext = () => {
    if (index >= docs.length - 1) {
      handleClose();
      return;
    }
    setIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (index <= 0) return;
    setIndex((i) => i - 1);
  };

  // Always points at the latest closure so the timer never uses a stale index.
  goNextRef.current = goNext;

  // ── Likes ────────────────────────────────────────────────────────────

  const toggleLike = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    const docId = current.id;
    const data = current.data();
    const thumbnailUrl =
      data?.type === 'image' ? data?.imageUrl ?? null : null;

    const optimistic = !isLiked;
    setIsLiked(optimistic);
    try {
      const result = await StatusWantService.toggleLike(
        StatusWantType.status,
        docId,
        { ownerUid: uid, thumbnailUrl }
      );
      setIsLiked(result);
    } catch (e) {
      console.warn('[StatusViewer] toggleLike failed', e);
      setIsLiked(!optimistic);
    }
  };

  // ── Share ────────────────────────────────────────────────────────────

  const handleShare = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    setIsPaused(true);
    const docId = current.id;
    const shareText = `Check out ${
      isOwner ? 'my' : `${ownerName}'s`
    } status on Filatom!\nhttps://filatom.com/status/${docId}`;

    try {
      if (navigator.share) {
        await navigator.share({ text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
      }
      await StatusWantService.incrementShareCount(
        StatusWantType.status,
        docId
      );
    } catch (_) {
      // ignore cancellations/errors
    } finally {
      setIsPaused(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────

  const deleteCurrentStatus = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    const docId = current.id;
    try {
      await deleteDoc(doc(db, 'status', docId));
      const newDocs = docs.filter((d) => d.id !== docId);
      const newIndex =
        newDocs.length > 0 && index >= newDocs.length
          ? newDocs.length - 1
          : index;
      setDocs(newDocs);
      setIndex(newIndex);
      if (newDocs.length === 0) {
        setTimeout(() => handleClose(), 500);
      } else {
        elapsedRef.current = 0;
        setProgress(0);
        setIsPaused(false);
      }
    } catch (_) {
      setIsPaused(false);
    }
  };

  // ── Viewers ──────────────────────────────────────────────────────────

  const openViewers = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    setIsPaused(true);
    try {
      const list = await StatusWantService.viewersFor(
        StatusWantType.status,
        current.id
      );
      setViewers(list);
    } catch (e) {
      console.warn('[StatusViewer] viewersFor failed', e);
      setViewers([]);
    }
    setShowViewers(true);
  };

  // ── Open owner profile ───────────────────────────────────────────────

  const openOwnerProfile = () => {
    if (isOwner) return;
    setIsPaused(true);
    if (onOpenProfile) onOpenProfile(uid);
  };

  // ── Render ───────────────────────────────────────────────────────────

  const overlayBase = {
    position: 'fixed',
    inset: 0,
    zIndex: 100,
    background: '#000',
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...overlayBase,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: `2px solid ${kOrange}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error or nothing active — show a message instead of hanging/blank.
  if (loadError || docs.length === 0) {
    return (
      <div
        style={{
          ...overlayBase,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
        }}
      >
        <p
          style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 14,
            textAlign: 'center',
            margin: 0,
          }}
        >
          {loadError ? `Couldn't load status: ${loadError}` : 'No active status.'}
        </p>
        <button
          onClick={handleClose}
          style={{
            background: kOrange,
            border: 'none',
            borderRadius: 20,
            padding: '8px 20px',
            color: '#000',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    );
  }

  const data = docs[index].data();
  const type = data?.type ?? 'text';

  // Round button treatment used only on desktop (side arrows + action column).
  const desktopRoundBtn = isDesktop
    ? {
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }
    : {};

  // Like / share / delete — placed inside the story on mobile and in a
  // column beside the story on desktop.
  const actionButtons = (
    <>
      <button
        onClick={toggleLike}
        style={{
          background: 'none',
          border: 'none',
          color: isLiked ? '#FF1493' : '#fff',
          fontSize: 26,
          cursor: 'pointer',
          padding: 0,
          ...desktopRoundBtn,
        }}
      >
        {isLiked ? '♥' : '♡'}
      </button>

      <button
        onClick={handleShare}
        style={{
          background: 'none',
          border: 'none',
          color: '#fff',
          fontSize: 24,
          cursor: 'pointer',
          padding: 0,
          ...desktopRoundBtn,
        }}
      >
        ➤
      </button>

      {isOwner && (
        <button
          onClick={() => {
            setIsPaused(true);
            setShowDeleteDialog(true);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: 24,
            cursor: 'pointer',
            padding: 0,
            ...desktopRoundBtn,
          }}
        >
          🗑
        </button>
      )}
    </>
  );

  const rootStyle = isDesktop
    ? {
        ...overlayBase,
        background: '#0B0C0E',
        overflow: 'hidden',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    : {
        ...overlayBase,
        overflow: 'hidden',
        userSelect: 'none',
      };

  // Mobile: the story fills the screen. Desktop: a centered 9:16 card.
  const storyStyle = isDesktop
    ? {
        position: 'relative',
        width: DESKTOP_CARD_WIDTH,
        height: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        background: '#000',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        flexShrink: 0,
      }
    : {
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
      };

  const story = (
    <div style={storyStyle}>
      {/* Tap regions */}
      <div
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const w = rect.width;
          if (x < w / 3) goPrev();
          else if (x > (w * 2) / 3) goNext();
        }}
        onPointerDown={() => setIsPaused(true)}
        onPointerUp={() => setIsPaused(false)}
        onPointerCancel={() => setIsPaused(false)}
        onPointerLeave={() => setIsPaused(false)}
        style={{ position: 'absolute', inset: 0 }}
      >
        {type === 'image' ? (
          <img
            src={data?.imageUrl ?? ''}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: `#${(data?.backgroundColor ?? 0x23262c)
                .toString(16)
                .padStart(6, '0')
                .slice(-6)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 32px',
            }}
          >
            <p
              style={{
                color: '#fff',
                fontSize: 24,
                fontWeight: 600,
                textAlign: 'center',
                margin: 0,
              }}
            >
              {data?.text ?? ''}
            </p>
          </div>
        )}

        {type === 'image' && data?.caption && (
          <div
            style={{
              position: 'absolute',
              left: 16,
              right: 16,
              bottom: 28,
              color: '#fff',
              fontSize: 14,
            }}
          >
            {data.caption}
          </div>
        )}
      </div>

      {/* Progress bars */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          left: 10,
          right: 10,
          display: 'flex',
          gap: 4,
          pointerEvents: 'none',
        }}
      >
        {docs.map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 2.5,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width:
                  i < index
                    ? '100%'
                    : i === index
                    ? `${progress * 100}%`
                    : '0%',
                height: '100%',
                background: '#fff',
                borderRadius: 2,
                transition: 'width 0.05s linear',
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
          left: 12,
          right: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <div
          onClick={openOwnerProfile}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: isOwner ? 'default' : 'pointer',
            minWidth: 0,
          }}
        >
          <UserAvatar size={34} imageUrl={ownerAvatarUrl} name={ownerName} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isOwner ? 'Your Status' : ownerName}
              </span>
              {currentTimeLabel && (
                <>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                    ·
                  </span>
                  <span
                    style={{
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: 11.5,
                      fontWeight: 500,
                    }}
                  >
                    {currentTimeLabel}
                  </span>
                </>
              )}
            </div>
            {!isOwner && ownerUsername && (
              <div
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 11.5,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ownerUsername}
              </div>
            )}
          </div>
        </div>

        {isOwner && (
          <button
            onClick={openViewers}
            style={{
              background: 'rgba(0,0,0,0.4)',
              border: 'none',
              borderRadius: 14,
              padding: '6px 10px',
              color: '#fff',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
            }}
          >
            <EyeIcon />
            Views
          </button>
        )}

        {/* On desktop the close button is pinned to the page corner instead. */}
        {!isDesktop && (
          <button
            onClick={handleClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.4)',
              border: 'none',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Bottom actions (mobile only — desktop shows them beside the card) */}
      {!isDesktop && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 18,
          }}
        >
          {actionButtons}
        </div>
      )}

      {/* Viewers bottom sheet */}
      {showViewers && (
        <div
          onClick={() => {
            setShowViewers(false);
            setIsPaused(false);
          }}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              background: '#16181C',
              borderRadius: '20px 20px 0 0',
              padding: 16,
              maxHeight: isDesktop ? '70%' : '70vh',
              overflowY: 'auto',
            }}
          >
            <h3
              style={{
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                margin: '0 0 12px 0',
              }}
            >
              {viewers.length} {viewers.length === 1 ? 'view' : 'views'}
            </h3>
            {viewers.length === 0 ? (
              <p
                style={{
                  color: 'rgba(255,255,255,0.38)',
                  fontSize: 13,
                  padding: '20px 0',
                }}
              >
                No views yet
              </p>
            ) : (
              viewers.map((v, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '6px 0',
                  }}
                >
                  <UserAvatar
                    size={34}
                    imageUrl={v.photoUrl}
                    name={v.displayName}
                  />
                  <span style={{ color: '#fff', fontSize: 13 }}>
                    {v.displayName ?? 'User'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Delete dialog */}
      {showDeleteDialog && (
        <div
          onClick={() => {
            setShowDeleteDialog(false);
            setIsPaused(false);
          }}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: kCard,
              borderRadius: 16,
              padding: '24px 20px 16px',
              maxWidth: 320,
              width: '100%',
            }}
          >
            <h3
              style={{
                color: '#fff',
                fontSize: 17,
                fontWeight: 700,
                margin: '0 0 10px 0',
                textAlign: 'center',
              }}
            >
              Delete Status
            </h3>
            <p
              style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: 14,
                lineHeight: 1.5,
                textAlign: 'center',
                margin: '0 0 20px 0',
              }}
            >
              This status will be permanently removed.
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setIsPaused(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 14,
                  cursor: 'pointer',
                  padding: '8px 12px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  deleteCurrentStatus();
                }}
                style={{
                  background: 'rgba(255,82,82,0.15)',
                  border: 'none',
                  color: '#ff5252',
                  fontWeight: 700,
                  fontSize: 14,
                  borderRadius: 8,
                  padding: '8px 16px',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Mobile: unchanged full-screen story ──────────────────────────────

  if (!isDesktop) {
    return (
      <div
        style={rootStyle}
        onTouchStart={(e) => {
          touchStartY.current = e.touches[0].clientY;
        }}
        onTouchEnd={(e) => {
          const deltaY = e.changedTouches[0].clientY - touchStartY.current;
          if (deltaY > 80) handleClose();
        }}
      >
        {story}
      </div>
    );
  }

  // ── Desktop: [prev] [ 9:16 story card ] [next + actions] ─────────────

  const arrowBtn = {
    ...desktopRoundBtn,
    border: 'none',
    color: '#fff',
    fontSize: 22,
    cursor: 'pointer',
    padding: 0,
  };

  return (
    <div
      style={rootStyle}
      onClick={(e) => {
        // Click on the dark backdrop closes the viewer.
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      {/* Close — pinned to the top-right of the page */}
      <button
        onClick={handleClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 20,
          right: 24,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          color: '#fff',
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        ✕
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 24,
          height: DESKTOP_STAGE_HEIGHT,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        {/* Left column: previous */}
        <div
          style={{
            width: 48,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {index > 0 && (
            <button onClick={goPrev} aria-label="Previous status" style={arrowBtn}>
              ‹
            </button>
          )}
        </div>

        {story}

        {/* Right column: next (centered) + actions (bottom) */}
        <div
          style={{
            width: 48,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 18,
          }}
        >
          <button
            onClick={goNext}
            aria-label="Next status"
            style={{
              ...arrowBtn,
              position: 'absolute',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
            }}
          >
            ›
          </button>
          {actionButtons}
        </div>
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
        stroke="#fff"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" />
    </svg>
  );
}